import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Form, InputNumber, Row, Col, Select, message } from 'antd';
import Icon from '@ant-design/icons';
import styles from './AddAmountModal.module.less';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import LockIcon from '../../assets/img/blueLockIcon.svg?react';
import { useHistory } from 'react-router-dom';
import {
  CreateWalletInvoiceRequest,
  WalletInvoiceResponse,
  PaymentMethod,
  PaymentType
} from '../../api/models/payments';

import { createPurchaseWithCard } from '../../api/payments';
import {
  addAmountScreenCancelClicked,
  addAmountScreenNextClicked,
  addAmountScreenOpen
} from '../../utils/analytika/events';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../types/store';
import { userSelector } from '../../redux/selectors/user';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import { getErrorFromResponse } from '../../utils/apiHelpers';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import PaymentMethodIcon from '../PaymentMethodIcon/PaymentMethodIcon';

interface AddAmountModalProps {
  visible: boolean;
  onClose: () => void;
  currency?: string;
  createWalletInvoice?: (request: CreateWalletInvoiceRequest) => Promise<WalletInvoiceResponse>;
}

const AddAmountModal = ({ visible, onClose }: AddAmountModalProps) => {
  const { t } = useTranslation();
  const [isFormSending, setIsFormSending] = useState(false);
  const [amount, setAmount] = useState<number>(0);
  const [selectedCardId, setSelectedCardId] = useState<number | undefined>(undefined);
  const [cvc, setCvc] = useState<number | undefined>(undefined);
  const history = useHistory();

  const {
    paymentMethods,
    defaultPaymentMethodId,
    userGalileoVars,
    userWalletConfig: {
      userBalance: { currency }
    }
  } = useSelector(userSelector);

  const {
    invoices: { createWalletInvoice }
  } = useDispatch<AppDispatch>();

  const { fireEvent } = useAnalytika();

  // Pre-select card: default card if it's a credit card, otherwise first card
  useEffect(() => {
    if (userGalileoVars?.[FeatureFlag.IS_ONE_CLICK_DISABLED]) {
      const creditCards = paymentMethods?.filter((m: PaymentMethod) => m.paymentType === PaymentType.CREDIT_CARD);
      if (creditCards && creditCards.length > 0) {
        const defaultCard = creditCards.find((card: PaymentMethod) => card.id === defaultPaymentMethodId);
        if (defaultCard) {
          setSelectedCardId(defaultCard.id);
        } else {
          setSelectedCardId(creditCards[0].id);
        }
      }
    }
  }, [paymentMethods, defaultPaymentMethodId, userGalileoVars]);

  useEffect(() => {
    visible && fireEvent(addAmountScreenOpen());
  }, [visible]);

  const handleCancel = () => {
    fireEvent(addAmountScreenCancelClicked({ amount_text: amount }));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CANCEL_WALLET,
      event_version: 2,
      wallet_balance: amount,
      wallet_currency: currency
    });
    onClose();
  };

  const handleAmountChange = (amount: number | null) => {
    setAmount(amount || 0);
  };

  const handleSubmit = async () => {
    if (!amount) return;
    try {
      setIsFormSending(true);
      const isOneClickDisabled = Boolean(userGalileoVars?.[FeatureFlag.IS_ONE_CLICK_DISABLED]);
      const { id } = await createWalletInvoice({ amount, currency });
      sessionStorage.setItem('wallet_invoice_id', id);
      fireEvent(addAmountScreenNextClicked({ amount_text: amount, transaction_id: id }));
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_NEXT_ADD_CREDIT, event_version: 2, amount, currency });

      // New flow: when one-click is disabled, create a purchase with card
      if (isOneClickDisabled) {
        if (selectedCardId) {
          try {
            const purchaseWithCardResponse = await createPurchaseWithCard({
              invoiceId: id,
              cardId: selectedCardId,
              cvc: cvc
            });
            if (purchaseWithCardResponse?.issuerUrl) {
              window.location.assign(purchaseWithCardResponse.issuerUrl);
              return;
            }
            window.dispatchEvent(new Event('wallet-invoice-updated'));
            setIsFormSending(false);
            onClose();
            return;
          } catch (err) {
            sessionStorage.removeItem('wallet_invoice_id');
            message.error({ content: getErrorFromResponse(err as any) });
            setIsFormSending(false);
            onClose();
            return;
          }
        }
        setIsFormSending(false);
      }
      // Old flow: redirect to One Click Invoice page
      else {
        setIsFormSending(false);
        history.push(`/invoices/${id}`);
      }
    } catch (err) {
      message.error({ content: getErrorFromResponse(err as any) });
      setIsFormSending(false);
      onClose();
    }
  };
  return (
    <Modal
      width={483}
      title={t('modal.addAmount.title')}
      visible={visible}
      onClose={onClose}
      footer={[
        <Button
          key="back"
          size={'large'}
          disabled={isFormSending}
          onClick={handleCancel}
          data-testid="buttonCancelModal"
        >
          {t('general.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          size={'large'}
          disabled={isFormSending || !amount || amount < 1}
          loading={isFormSending}
          onClick={handleSubmit}
          data-testid="buttonSubmitModal"
        >
          {t('modal.addAmount.nextBtn')}
        </Button>
      ]}
    >
      <div className={styles.content}>
        <Row>
          <Col xs={24} md={24} lg={24}>
            <Form id="addAmountForm" data-testid="addAmountForm" layout="vertical" hideRequiredMark>
              <Row>
                <Col xs={24} md={24} lg={24}>
                  <Form.Item label={`${t('modal.addAmount.amountFieldTitle')} (${currency})`} data-testid="userIdItem">
                    <InputNumber
                      data-testid="amountInput"
                      size={'large'}
                      step={10}
                      min={0}
                      precision={0}
                      value={amount}
                      placeholder={t('modal.addAmount.placeholder')}
                      onChange={handleAmountChange}
                    />
                  </Form.Item>
                  {Boolean(userGalileoVars?.[FeatureFlag.IS_ONE_CLICK_DISABLED]) && (
                    <>
                      <Form.Item label={t('modal.addAmount.selectCard')}>
                        <Select
                          className={styles.cardSelect}
                          value={selectedCardId}
                          onChange={value => setSelectedCardId(value)}
                          placeholder={t('modal.addAmount.selectCardPlaceholder')}
                          data-testid="cardSelectDropdown"
                          optionLabelProp="label"
                          options={paymentMethods
                            ?.filter((m: PaymentMethod) => m.paymentType === PaymentType.CREDIT_CARD)
                            // @ts-ignore
                            ?.map((card: PaymentMethod) => ({
                              value: card.id,
                              label: (
                                <div className={styles.cardOption}>
                                  <PaymentMethodIcon className={styles.cardOptionIcon} cardType={card.cardType} />
                                  <div className={styles.cardOptionTexts}>
                                    <Typography.Text strong>{card.display}</Typography.Text>
                                    <Typography.Text type="secondary" className={styles.cardOptionExpiry}>
                                      {t('payments.expiresOn', { expiresOn: card.expiryDate })}
                                    </Typography.Text>
                                  </div>
                                </div>
                              )
                            }))}
                        />
                      </Form.Item>
                      {paymentMethods
                        ?.filter((m: PaymentMethod) => m.paymentType === PaymentType.CREDIT_CARD)
                        ?.find((card: PaymentMethod) => card.id === selectedCardId && card.isMada) && (
                        <Form.Item label="CVC">
                          <InputNumber
                            size={'large'}
                            min={0}
                            max={9999}
                            precision={0}
                            value={cvc}
                            onChange={(value: number | null) => setCvc(value === null ? undefined : value)}
                            placeholder={t('modal.addAmount.cvcPlaceholder')}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      )}
                    </>
                  )}

                  <div className={styles.secureNotice}>
                    <Icon component={() => <LockIcon />} />
                    <Typography.Text className={styles.noticeText}>
                      {t('modal.addAmount.securityNotice')}
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default AddAmountModal;
